import { Component, Vue, Watch } from 'vue-property-decorator';
import ErrorHandler from '@/support/ErrorHandler';
import MiFileUpload from '@/components/mi-file-upload/MiFileUpload';
import { addClass, removeClass } from '@/support/Html';
import { Brand } from '@/models/Brand';
import { Supplier } from '@/models/Supplier';
import debounce from 'debounce';
import { Product } from '@/models/Product';
import { axiosDownload } from '@/support/Client';

@Component<UploadProductCsvDialog>({
  components: {
    MiFileUpload,
  },
})
export default class UploadProductCsvDialog extends Vue {
  //#region @Props
  /**
  * ie.
  * @Prop()
  * protected user!: User
  */
  //#endregion


  //#region @Refs
  /**
  * ie.
  * @Ref()
  * readonly anotherComponent!: AnotherComponent
  */
  //#endregion


  //#region Class properties
  /**
  * ie.
  * protected isLoading = true;
  * * protected company: Company | null = null;
  */

  protected currentStep: number = 1;

  protected selected: any[] = [];

  protected isLoading: boolean = false;
  protected isUploadingCsv: boolean = false;
  protected activeStepper: boolean = true;

  protected selectedCsvFile: File | null = null;
  protected uploadResponse: object | null = {};

  protected brands: Brand[] = [];
  protected suppliers: Supplier[] = [];
  protected selectedBrand: Brand | null = null;
  protected selectedSupplier: Supplier | null = null;

  protected acceptedFileTypes = [
    'text/csv',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
  ];
  protected uploadedFile: string = '';

  public searchBrandDebounce: Function = this.handleBrandSearch();
  public searchSupplierDebounce: Function = this.handleSupplierSearch();
  protected brandSearch: string = '';
  protected supplierSearch: string = '';
  //#endregion


  //#region Lifecycle Hooks / Init
  /**
  * ie.
  * protected mounted(): void {
  *  console.log('MyComponent has Mounted');
  * }
  */


  protected mounted(): void {
    const pageHtml = document.querySelectorAll('html')[0];
    addClass(pageHtml, 'overflow-y-hidden');
    this.getBrands();
    this.getSuppliers();
  }

  //#endregion


  //#region Class methods
  /**
  * ie.
  * protected toggleLoading(): void {
  *  this.isLoading = ! this.isLoading;
  * }
  */

  protected handleBrandSearch(): any {
    return debounce((searchInput: string) => {
      this.getBrands(searchInput);
    }, 400);
  }

  protected handleSupplierSearch(): any {
    return debounce((searchInput: string) => {
      this.getSuppliers(searchInput);
    }, 400);
  }

  protected goToStep(step: number): void {
    if (this.currentStep < step || !this.activeStepper) {
      return;
    }

    this.currentStep = step;
  }

  protected close(): void {
    this.$emit('input', false);
    this.currentStep = 1;
    this.clear();
    const pageHtml = document.querySelectorAll('html')[0];
    removeClass(pageHtml, 'overflow-y-hidden');
  }

  protected clear(): void {
    this.selectedBrand = null;
    this.selectedSupplier = null;
    this.selectedCsvFile = null;
  }

  protected handleItemDropped(file: File): void {
    if(!file) {
      return;
    }

    this.selectedCsvFile = file;
    const fileUploadComponent = this.$refs.fileUpload as MiFileUpload;
    fileUploadComponent.uploaded();
  }
  //#endregion


  //#region Async methods
  /**
  * ie.
  * protected async fetchUserCompany(): Promise<void> {
  *  this.company = await new Company().filter({user: this.user.id}).all();
  * }
  */

  protected async getBrands(filter: string = ''): Promise<void> {
    try {
      this.brands = await new Brand()
        .filter('search', filter)
        .limit(100)
        .all()
    } catch(error) {
      ErrorHandler.network(error);
    }
  }

  protected async getSuppliers(filter: string = ''): Promise<void> {
    try {
      this.suppliers = await new Supplier()
        .filter('search', filter)
        .limit(100)
        .all()
    } catch(error) {
      ErrorHandler.network(error);
    }
  }


  protected async create(): Promise<void> {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.currentStep = 4;

    const payload = {
      brand: this.selectedBrand ? this.selectedBrand.id : '',
      supplier: this.selectedSupplier ? this.selectedSupplier.id : '',
      products: this.selectedCsvFile,
    };

    try {
      this.uploadResponse = await new Product()
        .importProducts()
        .create(payload)

    } catch (error) {
      ErrorHandler.network(error);
      this.currentStep = 3;
    }

    this.isLoading = false;
  }

  protected async downloadTemplateCsv(): Promise<void> {
    try {
      await axiosDownload(
        {
          url: new Product().exportProducts(),
          accept: 'text/csv; charset=utf-8'
        },
        {}
      );
    } catch (error) {
      ErrorHandler.network(error);
    }
  }

  //#endregion


  //#region Getters & Setters
  /**
  * ie.
  * protected get companyName(): string {
  *  return this?.company.name || 'N/A';
  * }
  */


  protected get hasBrandAndSupplier() {
    return !!this.selectedBrand && !!this.selectedSupplier;
  }

  //#endregion


  //#region @Watchers
  /**
  * ie.
  * @Watch('company')
  * protected companyChanged() {
  *  console.log('Company Changed') ;
  * }
  */

   @Watch('brandSearch')
   protected brandSearchChanged(brand: string) {
     this.searchBrandDebounce(brand);
   }

   @Watch('supplierSearch')
   protected supplierSearchChanged(brand: string) {
     this.searchSupplierDebounce(brand)
   }
  //#endregion
}
